/* eslint-disable @typescript-eslint/no-explicit-any */
import { error as logErrorToConsole } from '@ms/yammer-console-logging';
import { getTelemetryHostingConfig } from '@ms/yammer-telemetry-store';
import type { ErrorEventProperties, ErrorEvent as TelemetryErrorEvent } from '@ms/yammer-telemetry-support';
import { isNil } from 'lodash';

import { emitTelemetryEvent, emitTelemetryEventNow } from '../report/eventSink';

import { filterUnsafeProperties } from './eventPropertyCheck';

export type ReportableError = Error | TelemetryErrorEvent | PromiseRejectionEvent;

const getErrorWithoutMessage = (error: Error) => {
  const { message: _message, ...errorWithoutMessage } = error;

  return errorWithoutMessage;
};

const getTelemetryErrorEventProperties = (
  error: ReportableError,
  eventProperties: ErrorEventProperties
): TelemetryErrorEvent['properties'] => {
  if (error instanceof ErrorEvent) {
    return {
      line: error.lineno,
      col: error.colno,
      file: error.filename,
      ...eventProperties,
    };
  }

  // istanbul ignore if
  if (error instanceof PromiseRejectionEvent) {
    return {
      reason: error.reason,
      ...eventProperties,
    };
  }

  // istanbul ignore else
  if (error instanceof Error) {
    return { ...getErrorWithoutMessage(error), ...eventProperties };
  }

  // istanbul ignore next
  return eventProperties;
};

type GetErrorMessage = (error: any) => string | undefined;
export const getErrorMessage: GetErrorMessage = (error) => {
  if (isNil(error)) {
    return;
  }

  if (typeof error === 'object') {
    if ('message' in error) {
      return error.message;
    }

    return JSON.stringify(error);
  }

  return error.toString();
};

type GetErrorReason = (error: any) => string | undefined;
export const getErrorReason: GetErrorReason = (error) => {
  if (!error) {
    return;
  }

  return error.reason || error.errorCode;
};

const getErrorStack = (error: ReportableError): string => {
  const errorObject = error instanceof ErrorEvent ? error.error : error;

  return errorObject?.stack || '';
};

export const getErrorName = (error: ReportableError): string => {
  if (error instanceof ErrorEvent) {
    return 'ErrorEvent';
  }

  // istanbul ignore if
  if (error instanceof PromiseRejectionEvent) {
    return 'PromiseRejectionEvent';
  }

  return error.name;
};

export const getTelemetryErrorEvent = (options: ReportErrorOptions): TelemetryErrorEvent => {
  const { error, eventProperties = {}, skipAuthentication } = options;
  const name = getErrorName(error);

  return {
    type: 'Error',
    name,
    occurredAt: Date.now().toString(),
    message: getErrorMessage(error) || '',
    stack: getErrorStack(error),
    skipAuthentication,
    properties: filterUnsafeProperties<TelemetryErrorEvent['properties']>(
      name,
      getTelemetryErrorEventProperties(error, eventProperties)
    ),
  };
};

export interface ReportErrorOptions {
  /**
   * One of the following error objects can be reported.
   * -  An [Error](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error)
   *    object consisting of message and stack trace for the error. This object is the preferred way to report errors.
   * -  An [ErrorEvent](https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent) object
   *    representing event providing information related to errors in scripts or in files.
   *    This property should be passed whenever it is already available.
   */
  readonly error: ReportableError;
  /**
   * The various properties that are used to describe details around a reported error. These are
   * used to glean details about the circumstance that led to the error.
   */
  readonly eventProperties?: ErrorEventProperties;
  /**
   * Whether to skip authentication and use a fallback token for reporting this error.
   * This should be set to true when reporting authentication-related errors to avoid circular dependencies.
   * Defaults to false.
   */
  readonly skipAuthentication?: boolean;
  /**
   * Whether or not the error should be sent to the console as well. Defaults to true.
   */
  readonly sendToConsole?: boolean;
}

interface ReportUnknownErrorOptions extends Omit<ReportErrorOptions, 'error'> {
  readonly error: unknown;
}

export const isReportableError = (error: unknown): error is ReportableError =>
  error instanceof Error || error instanceof ErrorEvent || error instanceof PromiseRejectionEvent;

type CastToReportableError = (exception: unknown) => ReportableError;
const castToReportableError: CastToReportableError = (exception) => {
  if (isReportableError(exception)) {
    return exception;
  }

  if (isNil(exception)) {
    return new Error();
  }

  const message = typeof exception === 'object' ? JSON.stringify(exception) : String(exception);

  return new Error(message);
};

type LogErrorToConsoleIfNecessary = (options: ReportErrorOptions) => void;
const logErrorToConsoleIfNecessary: LogErrorToConsoleIfNecessary = (options) => {
  const { sendToConsole = true } = options;
  const telemetryHostingConfig = getTelemetryHostingConfig();

  if (!telemetryHostingConfig?.hostingEnvironment) {
    return;
  }

  if (telemetryHostingConfig.hostingEnvironment === 'dev' && sendToConsole) {
    logErrorToConsole(options);
  }
};

interface ReportError {
  (options: ReportErrorOptions): void;
  (options: ReportUnknownErrorOptions): void;
}
export const reportError: ReportError = (options: ReportErrorOptions | ReportUnknownErrorOptions) => {
  const { error: exception, ...additionalOptions } = options;
  const error = castToReportableError(exception);
  const reportOptions: ReportErrorOptions = { ...additionalOptions, error };

  logErrorToConsoleIfNecessary(reportOptions);

  const errorEvent = getTelemetryErrorEvent(reportOptions);
  void emitTelemetryEvent(errorEvent);
};

interface ReportErrorNow {
  (options: ReportErrorOptions): Promise<void>;
  (options: ReportUnknownErrorOptions): Promise<void>;
}
export const reportErrorNow: ReportErrorNow = async (options: ReportErrorOptions | ReportUnknownErrorOptions) => {
  const { error: exception, ...additionalOptions } = options;
  const error = castToReportableError(exception);
  const reportOptions: ReportErrorOptions = { ...additionalOptions, error };

  logErrorToConsoleIfNecessary(reportOptions);

  const errorEvent = getTelemetryErrorEvent(reportOptions);
  await emitTelemetryEventNow(errorEvent);
};
